import { Alert, AlertProps, Snackbar } from '@mui/material'

import React, { useCallback, useState } from 'react'

type SnackbarState = {
  open: boolean
  severity: AlertProps['severity']
  content?: React.ReactNode
  autoHideDuration?: number | null
}

type ShowSnackbarOptions = {
  /** Auto-dismiss after this many ms. Pass `null` (default) for sticky behaviour. */
  autoHideDuration?: number | null
}

export const useSnackbar = () => {
  const [state, setState] = useState<SnackbarState>({ open: false, severity: 'info' })

  const showSnackbar = useCallback(
    (severity: AlertProps['severity'], content: React.ReactNode, options?: ShowSnackbarOptions) => {
      setState({
        open: true,
        severity,
        content,
        autoHideDuration: options?.autoHideDuration ?? null,
      })
    },
    [],
  )

  const handleClose = useCallback((_event?: unknown, reason?: string) => {
    if (reason === 'clickaway') return
    setState((current) => ({ ...current, open: false, content: undefined }))
  }, [])

  const SnackbarComponent = (
    <Snackbar
      open={state.open}
      onClose={handleClose}
      autoHideDuration={state.autoHideDuration ?? undefined}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Alert
        onClose={handleClose}
        severity={state.severity}
        variant="filled"
        sx={{ width: '100%' }}>
        {state.content}
      </Alert>
    </Snackbar>
  )

  return { showSnackbar, SnackbarComponent }
}
